import {
  File05Icon,
  Link01Icon,
  Star06Icon,
} from '@langchain/untitled-ui-icons';

import { ArrowRightIcon } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { CopyInlineLink } from '@/components/CopyInlineLink';
import { useEvaluatorStore } from '@/components/EvaluatorCrudPane/store/evaluatorStore';
import { Button, Text, Tooltip } from '@/design-system';
import {
  useAnnotationQueue,
  useAnnotationQueueSize,
  useAnnotationQueueTotalArchivedSize,
  useOrganizationId,
  useSessions,
} from '@/hooks/useSwr';
import { useAlignmentStore } from '@/stores/useAlignmentStore';
import {
  appAnnotationQueuesPath,
  appDatasetsPath,
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants';
import { hasExactlyOneBooleanKeyExcludingReasoning } from '@/utils/evaluator-utils';
import { pluralize } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

export const AnnotationQueueSection = ({
  alignmentQueueId,
  datasetId,
  sessionId,
}: {
  alignmentQueueId?: string;
  datasetId?: string;
  sessionId?: string;
}) => {
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const { setAlignmentMode, setAlignmentQueueId, setRunRuleId } =
    useAlignmentStore();

  const { data: queue } = useAnnotationQueue(alignmentQueueId);
  const { data: queueSize, isLoading: sizeLoading } =
    useAnnotationQueueSize(alignmentQueueId);
  const { data: queueArchivedSize, isLoading: archivedSizeLoading } =
    useAnnotationQueueTotalArchivedSize(alignmentQueueId);
  const totalCurrentAndArchivedRuns =
    (queueArchivedSize?.size ?? 0) + (queueSize?.size ?? 0);

  const { data } = useSessions(
    datasetId
      ? {
          reference_dataset: [datasetId],
          facets: false,
          limit: 1,
          include_stats: false,
        }
      : null,
    undefined,
    { revalidateOnFocus: false }
  );

  const experimentsOrRunsExist = datasetId
    ? (data?.rows ?? []).length > 0
    : sessionId;

  const { evaluatorId: runRuleId } = useParams();
  const feedbackSchema = useEvaluatorStore((state) => state.feedbackSchema);
  const isBinarySingleKeyEvaluator = feedbackSchema
    ? hasExactlyOneBooleanKeyExcludingReasoning(feedbackSchema)
    : false;

  const selectExperimentsOrRunsDisabled =
    (!datasetId && !sessionId) ||
    !experimentsOrRunsExist ||
    !isBinarySingleKeyEvaluator;

  const handleSelectExperimentsOrRuns = () => {
    if (selectExperimentsOrRunsDisabled) return;

    // Update alignment state in store
    setAlignmentMode(true);
    setAlignmentQueueId(alignmentQueueId);
    if (runRuleId) {
      setRunRuleId(runRuleId);
    }

    // Navigate to clean URL
    const targetPath = datasetId
      ? `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${datasetId}`
      : `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${sessionId}`;

    navigate(targetPath);
  };

  const selectExperimentsOrRunsButton = (
    <Button
      variant="underlined"
      color="primary"
      size="sm"
      rightDecorator={ArrowRightIcon}
      disabled={selectExperimentsOrRunsDisabled}
      onClick={handleSelectExperimentsOrRuns}
      className={cn(
        'bg-brand-tertiary',
        selectExperimentsOrRunsDisabled && 'cursor-not-allowed'
      )}
    >
      <Text>Select {datasetId ? 'Experiments' : 'Runs'}</Text>
    </Button>
  );

  const isAlignmentImpossible = !isBinarySingleKeyEvaluator;

  const content = (
    <div
      className={cn(
        'mx-auto w-[70%] rounded-lg border border-brand-subtle bg-brand-tertiary p-4 pb-[18px]',
        isAlignmentImpossible && 'cursor-not-allowed opacity-50'
      )}
    >
      <div className="flex gap-2">
        <Star06Icon className="h-4 w-4 text-brand-tertiary" />
        <div className="flex flex-col gap-1">
          <Text variant="h3" className="flex items-center">
            Align Evaluator with {datasetId ? 'experiment' : 'run'} data
            <Link
              to={
                'https://docs.langchain.com/langsmith/improve-judge-evaluator-feedback'
              }
              target="_blank"
            >
              <File05Icon className="ml-2 h-4 w-4" />
            </Link>
          </Text>
          <Text variant="body" className="text-xs">
            Select {datasetId ? 'experiments with existing outputs ' : 'runs '}
            to send to an annotation queue for human review.
          </Text>
          <div className="mt-2">
            {!experimentsOrRunsExist ? (
              <Tooltip
                description={
                  datasetId
                    ? 'Must have at least 1 experiment to align evaluator'
                    : 'Must have at least 1 run to align evaluator'
                }
              >
                {selectExperimentsOrRunsButton}
              </Tooltip>
            ) : (
              selectExperimentsOrRunsButton
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (archivedSizeLoading || sizeLoading) {
    return null;
  }

  return totalCurrentAndArchivedRuns === 0 || !alignmentQueueId ? (
    isAlignmentImpossible ? (
      <Tooltip title="Align Evaluator Flow is only available for boolean, single key evaluators.">
        {content}
      </Tooltip>
    ) : (
      content
    )
  ) : (
    <div
      className={cn(
        'mx-auto w-[70%] rounded-lg border border-secondary bg-primary px-6 py-6'
      )}
    >
      <div className="flex items-center gap-3 rounded-lg">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Text variant="h2" className="truncate">
              Annotation Queue: {queue?.name || 'Loading...'}
            </Text>
            <div className="text-sm text-brand-tertiary">
              {queueSize?.size ?? '0'} {pluralize('run', queueSize?.size ?? 0)}{' '}
              left to annotate
            </div>
          </div>
        </div>

        <div className="ml-auto flex flex-shrink-0 items-center gap-2">
          <Button
            disabled={!datasetId && !sessionId}
            color="secondary"
            size="md"
            onClick={() => {
              if (!datasetId && !sessionId) return;

              // Update alignment state in store
              setAlignmentMode(true);
              if (alignmentQueueId) {
                setAlignmentQueueId(alignmentQueueId);
              }
              if (runRuleId) {
                setRunRuleId(runRuleId);
              }

              // Navigate to clean URL
              const targetPath = datasetId
                ? `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${datasetId}`
                : `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${sessionId}`;

              navigate(targetPath);
            }}
          >
            Add To Queue
          </Button>

          <Link
            to={`/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${alignmentQueueId}`}
          >
            <Button variant="normal" color="secondary" size="md">
              Go To Queue
            </Button>
          </Link>
          <CopyInlineLink
            value={`${window.location.origin}/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${alignmentQueueId}`}
            prefix="URL"
            icon={<Link01Icon className="h-4 w-4" />}
            className="rounded-md border border-secondary p-2 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
          />
        </div>
      </div>
    </div>
  );
};
